import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Search, Filter, Image, FileText, X, Upload, Settings } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FilterSelect from '../components/FilterSelect';
import FilterInput from '../components/FilterInput';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminDeals = () => {
  const [deals, setDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [formData, setFormData] = useState({
    title: '', product: '', price: '', description: '',
    start_date: '', expiration_date: '', is_active: true, category_id: ''
  });
  
  // File upload state
  const [showFileModal, setShowFileModal] = useState(false);
  const [fileDeal, setFileDeal] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterOrder, setFilterOrder] = useState('newest');
  const [filterStatus, setFilterStatus] = useState('active');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dealsRes, categoriesRes] = await Promise.all([
        api.get('/admin/deals'),
        api.get('/deal-categories')
      ]);
      setDeals(dealsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        category_id: formData.category_id || null
      };
      
      if (editingDeal) {
        await api.put(`/admin/deals/${editingDeal.id}`, dataToSend);
        toast.success('Opportunity updated');
      } else {
        await api.post('/admin/deals', dataToSend);
        toast.success('Opportunity created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save opportunity');
    }
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setFormData({
      title: deal.title,
      product: deal.product,
      price: deal.price,
      description: deal.description || '',
      start_date: deal.start_date,
      expiration_date: deal.expiration_date,
      is_active: deal.is_active === true || deal.is_active === 1,
      category_id: deal.category_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this opportunity?')) return;
    try {
      await api.delete(`/admin/deals/${id}`);
      toast.success('Opportunity deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setEditingDeal(null);
    setFormData({
      title: '', product: '', price: '', description: '',
      start_date: '', expiration_date: '', is_active: true, category_id: ''
    });
  };

  // File upload handlers
  const openFileModal = (deal) => {
    setFileDeal(deal);
    setShowFileModal(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const currentPhotos = fileDeal.photos?.length || 0;
    if (currentPhotos + files.length > 5) {
      toast.error(`Maximum 5 photos allowed. You have ${currentPhotos}, trying to add ${files.length}`);
      return;
    }

    setUploadingPhotos(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    try {
      const res = await api.post(`/uploads/deals/${fileDeal.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${files.length} photo(s)`);
      setFileDeal({ ...fileDeal, photos: res.data.photos });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const currentDocs = fileDeal.documents?.length || 0;
    if (currentDocs + files.length > 5) {
      toast.error(`Maximum 5 documents allowed. You have ${currentDocs}, trying to add ${files.length}`);
      return;
    }

    setUploadingDocs(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    try {
      const res = await api.post(`/uploads/deals/${fileDeal.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${files.length} document(s)`);
      setFileDeal({ ...fileDeal, documents: res.data.documents });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setUploadingDocs(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (filename) => {
    try {
      const res = await api.delete(`/uploads/deals/${fileDeal.id}/photos/${filename}`);
      toast.success('Photo deleted');
      setFileDeal({ ...fileDeal, photos: res.data.photos });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleDeleteDoc = async (filename) => {
    try {
      const res = await api.delete(`/uploads/deals/${fileDeal.id}/documents/${filename}`);
      toast.success('Document deleted');
      setFileDeal({ ...fileDeal, documents: res.data.documents });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const isExpired = (date) => new Date(date) < new Date();

  // Apply filters
  const filteredDeals = deals
    .filter(deal => {
      if (filterCategory && deal.category_id !== filterCategory) return false;
      if (filterSearch) {
        const search = filterSearch.toLowerCase();
        if (!deal.title.toLowerCase().includes(search) && !deal.product.toLowerCase().includes(search)) return false;
      }
      // Status filter
      if (filterStatus === 'active') {
        if (!deal.is_active || isExpired(deal.expiration_date)) return false;
      } else if (filterStatus === 'inactive') {
        if (deal.is_active && !isExpired(deal.expiration_date)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (filterOrder === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (filterOrder === 'price') return (a.price || '').localeCompare(b.price || '');
      if (filterOrder === 'expiration') return new Date(a.expiration_date) - new Date(b.expiration_date);
      return 0;
    });

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Manage Opportunities</h1>
            <p className="text-gray-500 text-sm mt-1">Create and manage trading opportunities</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/deal-categories"
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium"
            >
              <Settings className="w-4 h-4" />
              Manage Categories
            </Link>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 text-sm"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Plus className="w-5 h-5" />
              New Opportunity
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 flex flex-wrap gap-4 items-center shadow-sm">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filters:</span>
          </div>
          
          <FilterSelect
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            options={[
              { value: 'active', label: 'Active Only' },
              { value: 'inactive', label: 'Inactive/Expired' },
              { value: 'all', label: 'All Status' }
            ]}
            data-testid="filter-status"
          />
          
          <FilterSelect
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            options={[
              { value: '', label: 'All Categories' },
              ...categories.map(cat => ({ value: cat.id, label: cat.name }))
            ]}
          />
          
          <FilterSelect
            value={filterOrder}
            onChange={(e) => setFilterOrder(e.target.value)}
            options={[
              { value: 'newest', label: 'Newest First' },
              { value: 'expiration', label: 'By Expiration' },
              { value: 'price', label: 'By Price' }
            ]}
          />
          
          <FilterInput
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            placeholder="Search opportunities..."
            className="flex-1 min-w-[200px]"
          />
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {editingDeal ? 'Edit Opportunity' : 'Create New Opportunity'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                  >
                    <option value="">No Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product *</label>
                  <input
                    type="text"
                    required
                    value={formData.product}
                    onChange={(e) => setFormData({...formData, product: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                  <input
                    type="text"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expiration *</label>
                    <input
                      type="date"
                      required
                      value={formData.expiration_date}
                      onChange={(e) => setFormData({...formData, expiration_date: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-gray-300"
                    style={{ accentColor: BRAND_BLUE }}
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">Cancel</button>
                  <button type="submit" className="flex-1 text-white px-4 py-2.5 rounded-xl font-medium text-sm" style={{ backgroundColor: BRAND_BLUE }}>{editingDeal ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* File Upload Modal */}
        {showFileModal && fileDeal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Manage Files: {fileDeal.title}</h2>
                <button onClick={() => setShowFileModal(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Photos Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Photos ({fileDeal.photos?.length || 0}/5)
                  </h3>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhotos || (fileDeal.photos?.length || 0) >= 5}
                    className="flex items-center gap-2 px-3 py-1.5 text-white text-sm rounded-xl disabled:opacity-50"
                    style={{ backgroundColor: BRAND_BLUE }}
                  >
                    <Upload className="w-4 h-4" />
                    {uploadingPhotos ? 'Uploading...' : 'Add Photos'}
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {(fileDeal.photos || []).map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={resolveFileUrl(photo.url, process.env.REACT_APP_BACKEND_URL)}
                        alt={photo.filename}
                        className="w-full h-24 object-cover rounded-xl border border-gray-200"
                      />
                      <button
                        onClick={() => handleDeletePhoto(photo.stored_name)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {(fileDeal.photos?.length || 0) === 0 && (
                    <p className="text-sm text-gray-400 col-span-4">No photos uploaded</p>
                  )}
                </div>
              </div>

              {/* Documents Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Documents ({fileDeal.documents?.length || 0}/5)
                  </h3>
                  <input
                    ref={docInputRef}
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={handleDocUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => docInputRef.current?.click()}
                    disabled={uploadingDocs || (fileDeal.documents?.length || 0) >= 5}
                    className="flex items-center gap-2 px-3 py-1.5 text-white text-sm rounded-xl disabled:opacity-50"
                    style={{ backgroundColor: BRAND_ORANGE }}
                  >
                    <Upload className="w-4 h-4" />
                    {uploadingDocs ? 'Uploading...' : 'Add PDFs'}
                  </button>
                </div>
                <div className="space-y-2">
                  {(fileDeal.documents || []).map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-red-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">{doc.filename}</p>
                          <p className="text-xs text-gray-400">{(doc.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteDoc(doc.stored_name)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-xl"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {(fileDeal.documents?.length || 0) === 0 && (
                    <p className="text-sm text-gray-400">No documents uploaded</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Deals Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Opportunity</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Category</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Price</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Files</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Expiration</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-center px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredDeals.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-gray-500">No opportunities found.</td></tr>
              ) : (
                filteredDeals.map((deal) => (
                  <tr key={deal.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{deal.title}</p>
                      <p className="text-xs text-gray-500">{deal.product}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {deal.category_name ? (
                        <span className="bg-gray-100 px-2.5 py-1 rounded-lg text-xs">{deal.category_name}</span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{deal.price}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Image className="w-3 h-3" /> {deal.photos?.length || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {deal.documents?.length || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{new Date(deal.expiration_date).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      {!deal.is_active ? (
                        <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-xs">Disabled</span>
                      ) : isExpired(deal.expiration_date) ? (
                        <span className="bg-red-50 text-red-600 px-2.5 py-1 rounded-full text-xs">Expired</span>
                      ) : (
                        <span className="bg-green-50 text-green-600 px-2.5 py-1 rounded-full text-xs">Active</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openFileModal(deal)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Manage Files">
                          <Upload className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEdit(deal)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(deal.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminDeals;
